// MBC: the Budget tab's Category column can be dragged wider or narrower. The width is a CSS variable on
// the page root, so dragging repaints only the column, not the whole budget table. It is remembered per
// device (localStorage). Double-click the handle to reset it.
import React, { useEffect, useRef, useState } from 'react';

import { theme } from '../../style';

const VAR = '--mbc-category-width';
const STORAGE_KEY = 'mbc-budget-category-width';
const CHANGED_EVENT = 'mbc-category-width-changed';
export const DEFAULT_CATEGORY_WIDTH = 200;
const MIN_WIDTH = 150;
const MAX_WIDTH = 500;

// Use in styles wherever the Category column used to be a fixed 200px.
export const categoryColumnWidth = `var(${VAR}, ${DEFAULT_CATEGORY_WIDTH}px)`;

function clamp(px: number) {
  return Math.round(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, px)));
}

function readStored(): number {
  try {
    const n = Number(window.localStorage.getItem(STORAGE_KEY));
    return n ? clamp(n) : DEFAULT_CATEGORY_WIDTH;
  } catch {
    return DEFAULT_CATEGORY_WIDTH;
  }
}

function apply(px: number) {
  document.documentElement.style.setProperty(VAR, `${px}px`);
}

function save(px: number) {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(px));
  } catch {
    // private window or blocked storage: the width still applies until reload
  }
  window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: px }));
}

// Apply the saved width as soon as the budget code loads, before anything paints.
let current = DEFAULT_CATEGORY_WIDTH;
if (typeof document !== 'undefined') {
  current = readStored();
  apply(current);
}

// The width as a number, updated when a drag ends (used to work out how many months fit).
export function useCategoryColumnWidth(): number {
  const [width, setWidth] = useState(current);
  useEffect(() => {
    const onChange = (e: Event) => setWidth((e as CustomEvent<number>).detail);
    window.addEventListener(CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CHANGED_EVENT, onChange);
  }, []);
  return width;
}

// The drag handle on the right edge of the Category header. Its parent must be position: relative.
export function CategoryColumnResizer() {
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);
  const [active, setActive] = useState(false);

  function set(px: number, done: boolean) {
    current = clamp(px);
    apply(current);
    if (done) save(current);
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the Category column"
      aria-valuemin={MIN_WIDTH}
      aria-valuemax={MAX_WIDTH}
      aria-valuenow={current}
      tabIndex={0}
      title="Drag to resize. Double-click to reset."
      onPointerDown={e => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { startX: e.clientX, startWidth: current };
        setActive(true);
      }}
      onPointerMove={e => {
        if (drag.current) {
          set(drag.current.startWidth + e.clientX - drag.current.startX, false);
        }
      }}
      onPointerUp={e => {
        if (drag.current) {
          e.currentTarget.releasePointerCapture(e.pointerId);
          drag.current = null;
          setActive(false);
          set(current, true);
        }
      }}
      onPointerCancel={() => {
        drag.current = null;
        setActive(false);
        set(current, true);
      }}
      onDoubleClick={() => set(DEFAULT_CATEGORY_WIDTH, true)}
      onKeyDown={e => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          set(current + (e.key === 'ArrowRight' ? 10 : -10), true);
        }
      }}
      className="mbc-category-resizer"
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: -4,
        width: 9,
        cursor: 'col-resize',
        zIndex: 2,
        touchAction: 'none',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        className="mbc-category-resizer-line"
        style={{
          width: 2,
          height: '100%',
          borderRadius: 1,
          backgroundColor: active ? theme.tableBorderSelected : 'transparent',
        }}
      />
      <style>{`.mbc-category-resizer:hover .mbc-category-resizer-line,
.mbc-category-resizer:focus-visible .mbc-category-resizer-line {
  background-color: ${theme.tableBorderHover} !important;
}
.mbc-category-resizer:focus-visible { outline: none; }`}</style>
    </div>
  );
}
